import { z } from "zod";

export const staffRoleSchema = z.enum(["Staff", "Manager", "Admin"]);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const profileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  full_name: z.string(),
  role: staffRoleSchema,
  /** Departments a manager may edit. Empty for staff; admins edit every department regardless. */
  managedDepartmentIds: z.array(z.string()),
});
export type Profile = z.infer<typeof profileSchema>;

export const subDivisionSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type SubDivision = z.infer<typeof subDivisionSchema>;

export const departmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  subDivisions: z.array(subDivisionSchema).optional(),
});
export type Department = z.infer<typeof departmentSchema>;

export const documentTypeSchema = z.enum([
  "PDF",
  "DOCX",
  "XLSX",
  "PPTX",
  "TXT",
  "JPG",
  "PNG",
  "MP4",
  "MOV",
  "WEBM",
]);
export type DocumentType = z.infer<typeof documentTypeSchema>;

export const documentContentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), body: z.string() }),
  z.object({ kind: z.literal("html"), html: z.string() }),
  // Uploaded files are fetched on demand through the data source; `url` is only
  // set for fixture files that ship with the app.
  z.object({ kind: z.literal("file"), url: z.string().optional() }),
  z.object({
    kind: z.literal("sheet"),
    columns: z.array(z.string()),
    rows: z.array(z.array(z.string())),
  }),
  z.object({
    kind: z.literal("slides"),
    slides: z.array(z.object({ title: z.string(), bullets: z.array(z.string()) })),
  }),
]);
export type DocumentContent = z.infer<typeof documentContentSchema>;

/** Who may read a document. Admins always can; enforced by RLS. */
export const documentVisibilitySchema = z.enum([
  "everyone",
  "managers",
  "department_managers",
  "admins",
]);
export type DocumentVisibility = z.infer<typeof documentVisibilitySchema>;

export const staffDocumentSchema = z.object({
  id: z.string(),
  departmentId: z.string(),
  subDivisionId: z.string().optional(),
  name: z.string(),
  category: z.string(),
  type: documentTypeSchema,
  lastUpdated: z.string(),
  /** When it was first added. */
  createdAt: z.string().optional(),
  content: documentContentSchema,
  featured: z.boolean().optional(),
  /** Absent means everyone. */
  visibility: documentVisibilitySchema.optional(),
  /** Set for uploaded files. */
  file: z
    .object({
      name: z.string(),
      mimeType: z.string(),
      sizeBytes: z.number(),
      /** The original in SharePoint; opens only for people with site access. */
      sharePointUrl: z.string().optional(),
    })
    .optional(),
});
export type StaffDocument = z.infer<typeof staffDocumentSchema>;

export const announcementSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  timestamp: z.string(),
});
export type Announcement = z.infer<typeof announcementSchema>;

export const activitySchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  date: z.string(),
});
export type Activity = z.infer<typeof activitySchema>;

export const managerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  departmentIds: z.array(z.string()),
  /** Invited by an admin but has not signed in yet. */
  pending: z.boolean().optional(),
  /** Granted by a department-manager Entra group; changes only in Entra. */
  ssoDepartmentIds: z.array(z.string()).optional(),
});
export type Manager = z.infer<typeof managerSchema>;

export const activityLogEntrySchema = z.object({
  id: z.string(),
  actorName: z.string(),
  /** `updated`: renamed, moved, re-scoped or rewritten. `replaced`: a new file version. */
  action: z.enum(["uploaded", "updated", "replaced", "deleted"]),
  documentName: z.string(),
  departmentLabel: z.string(),
  timestamp: z.string(),
});
export type ActivityLogEntry = z.infer<typeof activityLogEntrySchema>;

/**
 * A home page hot button: opens a department, optionally one tab and category,
 * between two optional dates (`YYYY-MM-DD`, inclusive).
 */
export const quickLinkSchema = z.object({
  id: z.string(),
  label: z.string(),
  departmentId: z.string(),
  subDivisionId: z.string().optional(),
  category: z.string().optional(),
  startsOn: z.string().optional(),
  endsOn: z.string().optional(),
});
export type QuickLink = z.infer<typeof quickLinkSchema>;

/** Payload for creating a document; the store assigns id and the timestamps. */
export const newDocumentSchema = staffDocumentSchema.omit({
  id: true,
  lastUpdated: true,
  createdAt: true,
  file: true,
});
export type NewDocument = z.infer<typeof newDocumentSchema>;

export const newAnnouncementSchema = announcementSchema.omit({ id: true, timestamp: true });
export type NewAnnouncement = z.infer<typeof newAnnouncementSchema>;

export const newActivitySchema = activitySchema.omit({ id: true });
export type NewActivity = z.infer<typeof newActivitySchema>;

export const newManagerSchema = managerSchema.omit({
  id: true,
  pending: true,
  ssoDepartmentIds: true,
});
export type NewManager = z.infer<typeof newManagerSchema>;

export const newQuickLinkSchema = quickLinkSchema.omit({ id: true });
export type NewQuickLink = z.infer<typeof newQuickLinkSchema>;
